import React, { useState, useEffect, useCallback } from "react";
import {
  WorkflowTemplate,
  WorkflowVariable,
  WorkflowConfiguration,
} from "../../types/workflow";
import { workflowService } from "../../services/workflowService";
import { integrationsService } from "../../services/integrationsService";
import { SimpleEditor } from "../../@/components/tiptap-templates/simple/simple-editor";
import { JSONContent } from "@tiptap/core/dist/types";
import InlineIntegrationConnector from "../integrations/InlineIntegrationConnector";
import IntegrationLogo from "../common/IntegrationLogo";
import { getIntegration } from "../../config/integrations";

interface CreateWorkflowModalProps {
  template: WorkflowTemplate;
  onClose: () => void;
  onWorkflowCreated: (workflow: any) => void;
}

const CreateWorkflowModal: React.FC<CreateWorkflowModalProps> = ({
  template,
  onClose,
  onWorkflowCreated,
}) => {
  const [step, setStep] = useState<
    "config" | "schedule" | "generate" | "review"
  >("config");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Connected integrations state
  const [connectedIntegrations, setConnectedIntegrations] = useState<
    Array<{ id: string; provider: string; name: string }>
  >([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(true);

  // Email generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<{
    step: string;
    progress: number;
    estimatedTimeRemaining: number;
  } | null>(null);
  const [generatedEmail, setGeneratedEmail] = useState<{
    subject: string;
    body: string | JSONContent;
  } | null>(null);

  const [workflowConfig, setWorkflowConfig] = useState<
    WorkflowConfiguration & { generateEmail?: boolean }
  >({
    name: template.name,
    description: "",
    variables: {},
    schedule: {
      enabled: false,
      cron: "0 9 * * 1", // Default: Every Monday at 9 AM
      timezone: "UTC",
    },
    notifications: {
      on_success: true,
      on_failure: true,
    },
    generateEmail: true,
  });

  // Load connected integrations on mount
  useEffect(() => {
    const loadIntegrations = async () => {
      setIsLoadingIntegrations(true);
      try {
        const userIntegrations = await integrationsService.getUserIntegrations();
        const transformed = userIntegrations
          .filter((i) => i.integration_status === "active")
          .map((i) => ({
            id: i.id,
            provider: i.provider,
            name: i.provider.charAt(0).toUpperCase() + i.provider.slice(1),
          }));
        setConnectedIntegrations(transformed);
      } catch (error) {
        console.error("Failed to load integrations:", error);
      } finally {
        setIsLoadingIntegrations(false);
      }
    };
    loadIntegrations();
  }, []);

  // Handle when a new integration is connected
  const handleIntegrationConnected = useCallback(async (provider: string) => {
    console.log(`[CreateWorkflowModal] New integration connected: ${provider}`);
    try {
      const userIntegrations = await integrationsService.getUserIntegrations();
      const transformed = userIntegrations
        .filter((i) => i.integration_status === "active")
        .map((i) => ({
          id: i.id,
          provider: i.provider,
          name: i.provider.charAt(0).toUpperCase() + i.provider.slice(1),
        }));
      setConnectedIntegrations(transformed);
    } catch (error) {
      console.error("Failed to refresh integrations:", error);
    }
  }, []);

  const handleScheduleChange = (field: string, value: any) => {
    setWorkflowConfig((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule!,
        [field]: value,
      },
    }));
  };

  const validateConfiguration = (): string[] => {
    const errors: string[] = [];

    if (!workflowConfig.name.trim()) {
      errors.push("Workflow name is required");
    }

    if (!workflowConfig.description?.trim()) {
      errors.push("Description is required");
    }

    // Validate cron expression if schedule is enabled
    if (workflowConfig.schedule?.enabled && !workflowConfig.schedule.cron) {
      errors.push("Schedule cron expression is required");
    }

    // Validate email generation step
    if (step === "review" && !generatedEmail) {
      errors.push("Please generate email content before proceeding");
    }

    return errors;
  };

  const handleEmailGeneration = async () => {
    setIsGenerating(true);
    setError(null);

    const progressSteps = [
      {
        step: "Analyzing workflow requirements...",
        progress: 10,
        duration: 10000,
      },
      { step: "Fetching integration data...", progress: 30, duration: 20000 },
      { step: "Processing data with AI...", progress: 60, duration: 40000 },
      { step: "Generating email content...", progress: 85, duration: 20000 },
    ];

    try {
      // Start the API call immediately
      const emailGenerationPayload = {
        templateId: template.id,
        name: workflowConfig.name,
        description: workflowConfig.description || "", // Ensure string type
        schedule: workflowConfig.schedule,
      };

      // Start the API call in parallel with progress
      const apiCallPromise = workflowService.generateWorkflowEmail(
        emailGenerationPayload
      );

      // Show progress animation up to 85%
      for (let i = 0; i < progressSteps.length; i++) {
        const currentStep = progressSteps[i];
        const remainingTime = progressSteps
          .slice(i + 1)
          .reduce((sum, step) => sum + step.duration, 0);

        setGenerationProgress({
          step: currentStep.step,
          progress: currentStep.progress,
          estimatedTimeRemaining: remainingTime,
        });

        await new Promise((resolve) =>
          setTimeout(resolve, currentStep.duration)
        );
      }

      // Keep at 85% and wait for actual API response
      setGenerationProgress({
        step: "Finalizing content...",
        progress: 85,
        estimatedTimeRemaining: 0,
      });

      // Wait for the actual API response
      const response = await apiCallPromise;

      console.log("🔍 API Response:", response);
      console.log("🔍 Response Data:", response.data);
      console.log("🔍 Response Keys:", Object.keys(response.data || {}));
      console.log("🔍 Response Data Type:", typeof response.data);
      console.log(
        "🔍 Response Data JSON:",
        JSON.stringify(response.data, null, 2)
      );

      if (response.success && response.data && response.data) {
        // More explicit data extraction
        const responseData = response.data;

        console.log("📧 Response Data Keys:", Object.keys(responseData));
        console.log("📧 Direct subject access:", responseData.subject);
        console.log("📧 Direct body access:", responseData.body);

        const subject = responseData.subject;
        const body = responseData.body;

        console.log("📧 Generated Email Subject:", subject);
        console.log("📧 Generated Email Body:", body);
        console.log("📧 Body Type:", typeof body);

        if (body) {
          console.log("📧 Body Structure:", JSON.stringify(body, null, 2));
        } else {
          console.log("📧 Body is null/undefined");
        }

        // Ensure we have valid content for the editor
        let editorContent: string | JSONContent;

        if (typeof body === "string") {
          // If body is a string, use it directly
          editorContent = body;
        } else if (body && typeof body === "object" && body.type === "doc") {
          // If body is already a JSONContent object, use it
          editorContent = body;
        } else {
          // Fallback: convert to string or provide default content
          editorContent = body ? String(body) : "Generated email content";
        }

        console.log("📝 Final Editor Content:", editorContent);

        setGeneratedEmail({
          subject: subject || "Generated Email",
          body: editorContent,
        });

        // Show 100% completion briefly before clearing progress
        setGenerationProgress({
          step: "Email generation complete!",
          progress: 100,
          estimatedTimeRemaining: 0,
        });

        // Clear progress after a brief moment
        setTimeout(() => setGenerationProgress(null), 1000);
      } else {
        throw new Error(response.error || "Failed to generate email");
      }
    } catch (err) {
      console.error("❌ Email generation error:", err);
      setError(err instanceof Error ? err.message : "Failed to generate email");
      setGenerationProgress(null);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCreateWorkflow = async () => {
    const validationErrors = validateConfiguration();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(", "));
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const workflowData = {
        ...workflowConfig,
        generatedEmail: generatedEmail,
      };

      const workflow = await workflowService.createWorkflowFromTemplate(
        template.id,
        workflowData
      );
      onWorkflowCreated(workflow);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create workflow"
      );
    } finally {
      setIsCreating(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case "config":
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workflow Name
              </label>
              <input
                type="text"
                value={workflowConfig.name}
                onChange={(e) =>
                  setWorkflowConfig((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                className="w-full border border-gray-300 px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter workflow name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={workflowConfig.description}
                onChange={(e) =>
                  setWorkflowConfig((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={4}
                className="w-full border border-gray-300 px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Describe what this workflow should accomplish, what data it should process, and what outcomes you expect..."
                required
              />
              <p className="mt-2 text-sm text-gray-600">
                💡 This description will be used to construct a tailored
                analysis of your tools and integrations, so please be as
                thorough as possible about your requirements and goals.
              </p>
            </div>

            {/* Connected Integrations Section */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Connected Integrations
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                This workflow will use data from your connected integrations.
              </p>
              <div className="bg-gray-50 p-4">
                {isLoadingIntegrations ? (
                  <div className="text-sm text-gray-500">Loading integrations...</div>
                ) : connectedIntegrations.length === 0 ? (
                  <div className="text-sm text-gray-500 mb-3">
                    No integrations connected yet. Connect at least one integration to use in this workflow.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {connectedIntegrations.map((integration) => {
                      const config = getIntegration(integration.provider);
                      return (
                        <div
                          key={integration.id}
                          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 text-sm"
                        >
                          {config && (
                            <IntegrationLogo
                              domain={config.domain}
                              name={config.name}
                              fallbackImage={config.icon}
                              className="w-4 h-4"
                            />
                          )}
                          <span>{integration.name}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <InlineIntegrationConnector
                  connectedProviders={connectedIntegrations.map((i) => i.provider)}
                  onConnectionSuccess={handleIntegrationConnected}
                  variant="button"
                  buttonText="Connect Integration"
                />
              </div>
            </div>

            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Template Overview
              </h4>
              <div className="bg-gray-50 p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Category:
                    </span>
                    <span className="ml-2 text-sm text-gray-900 capitalize">
                      {template.category.replace("_", " ")}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Complexity:
                    </span>
                    <span className="ml-2 text-sm text-gray-900 capitalize">
                      {template.complexity}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Steps:
                    </span>
                    <span className="ml-2 text-sm text-gray-900">
                      {template.config.steps.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Estimated Time:
                    </span>
                    <span className="ml-2 text-sm text-gray-900">
                      ~{Math.round(template.estimatedExecutionTime / 1000)}s
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "schedule":
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Schedule Configuration
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Configure when this workflow should run automatically.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  id="enable-schedule"
                  type="checkbox"
                  checked={workflowConfig.schedule?.enabled || false}
                  onChange={(e) =>
                    handleScheduleChange("enabled", e.target.checked)
                  }
                  className="border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label
                  htmlFor="enable-schedule"
                  className="text-sm font-medium text-gray-700"
                >
                  Enable automatic scheduling
                </label>
              </div>

              {workflowConfig.schedule?.enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Schedule
                    </label>
                    <select
                      value={workflowConfig.schedule.cron}
                      onChange={(e) =>
                        handleScheduleChange("cron", e.target.value)
                      }
                      className="w-full border border-gray-300 px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="0 9 * * 1">Every Monday at 9 AM</option>
                      <option value="0 9 * * *">Every day at 9 AM</option>
                      <option value="0 18 * * 5">Every Friday at 6 PM</option>
                      <option value="0 12 1 * *">
                        First day of every month at noon
                      </option>
                      <option value="*/30 * * * *">Every 30 minutes</option>
                      <option value="0 */4 * * *">Every 4 hours</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timezone
                    </label>
                    <select
                      value={workflowConfig.schedule.timezone}
                      onChange={(e) =>
                        handleScheduleChange("timezone", e.target.value)
                      }
                      className="w-full border border-gray-300 px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time</option>
                      <option value="America/Chicago">Central Time</option>
                      <option value="America/Denver">Mountain Time</option>
                      <option value="America/Los_Angeles">Pacific Time</option>
                      <option value="Europe/London">London</option>
                      <option value="Europe/Paris">Paris</option>
                      <option value="Asia/Tokyo">Tokyo</option>
                    </select>
                  </div>
                </>
              )}

              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-3">
                  Notifications
                </h5>
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <input
                      id="notify-success"
                      type="checkbox"
                      checked={
                        workflowConfig.notifications?.on_success || false
                      }
                      onChange={(e) =>
                        setWorkflowConfig((prev) => ({
                          ...prev,
                          notifications: {
                            ...prev.notifications!,
                            on_success: e.target.checked,
                          },
                        }))
                      }
                      className="border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label
                      htmlFor="notify-success"
                      className="text-sm text-gray-700"
                    >
                      Notify on successful execution
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      id="notify-failure"
                      type="checkbox"
                      checked={
                        workflowConfig.notifications?.on_failure || false
                      }
                      onChange={(e) =>
                        setWorkflowConfig((prev) => ({
                          ...prev,
                          notifications: {
                            ...prev.notifications!,
                            on_failure: e.target.checked,
                          },
                        }))
                      }
                      className="border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label
                      htmlFor="notify-failure"
                      className="text-sm text-gray-700"
                    >
                      Notify on failed execution
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case "generate":
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Generate First Email
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                AI will analyze your workflow requirements and generate the
                first email content based on your connected integrations and
                data sources.
              </p>
            </div>

            {/* Generation Progress */}
            {isGenerating && generationProgress && (
              <div className="bg-blue-50 border border-blue-200 p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="animate-spin h-8 w-8 border-b-2 border-blue-600"></div>
                  <div>
                    <h5 className="text-lg font-medium text-blue-900">
                      Generating Email Content
                    </h5>
                    <p className="text-sm text-blue-700">
                      {generationProgress.step}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-blue-700 mb-2">
                    <span>Progress</span>
                    <span>{generationProgress.progress}%</span>
                  </div>
                  <div className="w-full bg-blue-200 h-2">
                    <div
                      className="bg-blue-600 h-2 transition-all duration-500 ease-out"
                      style={{ width: `${generationProgress.progress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="text-sm text-blue-600">
                  ⏱️ Estimated time remaining:{" "}
                  {Math.ceil(
                    (generationProgress.estimatedTimeRemaining || 0) / 1000
                  )}{" "}
                  seconds
                </div>
              </div>
            )}

            {/* Generated Email Content */}
            {!isGenerating && generatedEmail && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-2 h-2 bg-green-500"></div>
                    <span className="text-sm font-medium text-green-800">
                      Email Generated Successfully
                    </span>
                  </div>
                  <p className="text-sm text-green-700">
                    Review and edit the generated content below before
                    proceeding.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Subject Line */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject Line
                    </label>
                    <input
                      type="text"
                      value={generatedEmail.subject}
                      onChange={(e) =>
                        setGeneratedEmail((prev) =>
                          prev ? { ...prev, subject: e.target.value } : null
                        )
                      }
                      className="w-full border border-gray-300 px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Email subject"
                    />
                  </div>

                  {/* Email Body Editor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Content
                    </label>
                    <div className="border border-gray-300 overflow-hidden">
                      {generatedEmail?.body ? (
                        <SimpleEditor
                          initialContent={generatedEmail.body}
                          onChange={(content) =>
                            setGeneratedEmail((prev) =>
                              prev ? { ...prev, body: content } : null
                            )
                          }
                        />
                      ) : (
                        <div className="p-4 text-center text-gray-500 bg-gray-50">
                          <div className="text-sm">
                            No content generated yet
                          </div>
                          <div className="text-xs mt-1">
                            Email content will appear here after generation
                          </div>
                        </div>
                      )}
                    </div>
                    {/* <p className="mt-2 text-sm text-gray-500">
                      💡 Dynamic variables and integration data have been
                      automatically included. You can edit or add more content
                      as needed.
                    </p> */}
                  </div>
                </div>
              </div>
            )}

            {/* Generation Not Started */}
            {!isGenerating && !generatedEmail && (
              <div className="text-center py-8 bg-gray-50 border-2 border-dashed border-gray-300">
                <div className="text-gray-400 text-4xl mb-4">🤖</div>
                <div className="text-gray-700 font-medium mb-2">
                  Ready to Generate Email
                </div>
                <div className="text-sm text-gray-500 mb-4">
                  Click &quot;Generate Email&quot; to create your first email
                  based on the workflow configuration.
                </div>
                <button
                  onClick={handleEmailGeneration}
                  disabled={isGenerating}
                  className="px-6 py-3 bg-indigo-600 text-white font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  🚀 Generate Email
                </button>
              </div>
            )}
          </div>
        );

      case "review":
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">
                Review Configuration
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Review your workflow configuration before creating.
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4">
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  Basic Information
                </h5>
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="font-medium">Name:</span>{" "}
                    {workflowConfig.name}
                  </div>
                  <div>
                    <span className="font-medium">Template:</span>{" "}
                    {template.name}
                  </div>
                  <div>
                    <span className="font-medium">Description:</span>{" "}
                    {workflowConfig.description || "None"}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4">
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  Schedule
                </h5>
                <div className="text-sm">
                  {workflowConfig.schedule?.enabled ? (
                    <>
                      <div>
                        <span className="font-medium">Cadence:</span>{" "}
                        {(() => {
                          switch (workflowConfig.schedule.cron) {
                            case "0 9 * * 1":
                              return "Every Monday at 9 AM";
                            case "0 9 * * *":
                              return "Every day at 9 AM";
                            case "0 18 * * 5":
                              return "Every Friday at 6 PM";
                            case "0 12 1 * *":
                              return "First day of every month at noon";
                            case "*/30 * * * *":
                              return "Every 30 minutes";
                            default:
                              return workflowConfig.schedule.cron;
                          }
                        })()}
                      </div>
                      <div>
                        <span className="font-medium">Timezone:</span>{" "}
                        {workflowConfig.schedule.timezone}
                      </div>
                    </>
                  ) : (
                    <span className="text-gray-600">Manual execution only</span>
                  )}
                </div>
              </div>

              {/* Generated Email Section */}
              {generatedEmail && (
                <div className="bg-gray-50 p-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-2">
                    Generated Email Content
                  </h5>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Subject:</span>{" "}
                      <span className="text-gray-700">
                        {generatedEmail.subject}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Content:</span>
                      <div className="mt-2 p-3 bg-white border border-gray-200 text-xs text-gray-600 max-h-32 overflow-y-auto">
                        Email content ready for sending (editable in previous
                        step)
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = (stepName: string) => {
    switch (stepName) {
      case "config":
        return "Basic Configuration";
      case "schedule":
        return "Schedule";
      case "generate":
        return "Email Generation";
      case "review":
        return "Review";
      default:
        return stepName;
    }
  };

  const steps = ["config", "schedule", "generate", "review"] as const;
  const currentStepIndex = steps.indexOf(step);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-6 border w-11/12 max-w-4xl shadow-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              Create Workflow
            </h3>
            <p className="text-sm text-gray-600">
              From template: {template.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <nav aria-label="Progress">
            <ol className="flex items-center justify-center">
              {steps.map((stepName, index) => (
                <li
                  key={stepName}
                  className={`relative ${
                    index !== steps.length - 1 ? "pr-8 sm:pr-20" : ""
                  }`}
                >
                  <div className="flex items-center">
                    <div
                      className={`relative flex h-8 w-8 items-center justify-center border-2 ${
                        index <= currentStepIndex
                          ? "border-indigo-600 bg-indigo-600 text-white"
                          : "border-gray-300 bg-white text-gray-500"
                      }`}
                    >
                      <span className="text-sm font-medium">{index + 1}</span>
                    </div>
                    <span
                      className={`ml-4 text-sm font-medium ${
                        index <= currentStepIndex
                          ? "text-indigo-600"
                          : "text-gray-500"
                      }`}
                    >
                      {getStepTitle(stepName)}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          </nav>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {/* Step Content */}
        <div className="mb-8 min-h-96">{renderStepContent()}</div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              if (currentStepIndex > 0) {
                setStep(steps[currentStepIndex - 1]);
              }
            }}
            disabled={currentStepIndex === 0}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>

            {currentStepIndex === steps.length - 1 ? (
              <button
                onClick={handleCreateWorkflow}
                disabled={isCreating}
                className="px-6 py-2 bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? "Creating..." : "Create Workflow"}
              </button>
            ) : (
              <button
                onClick={() => setStep(steps[currentStepIndex + 1])}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkflowModal;
